import React, { useState } from 'react';
import { EMZI_COLORS, EMZI_RADIUS, EMZI_SPACING } from '../../styles/emziCI';

/**
 * StructuredListInput - List input with min/max constraints and validation
 * @param {string} label - Field label
 * @param {Array} items - Current list items
 * @param {Function} onChange - Callback when items change
 * @param {number} minItems - Minimum required items
 * @param {number} maxItems - Maximum allowed items
 * @param {string} placeholder - Input placeholder
 * @param {string} helper - Helper text with example
 * @param {boolean} required - Is field required
 * @param {boolean} hasError - Show error state
 * @param {string} errorMessage - Error message to display
 */
const StructuredListInput = ({
  label,
  items = [],
  onChange,
  minItems = 0,
  maxItems = 20,
  placeholder = 'Add item...',
  helper = '',
  required = false,
  hasError = false,
  errorMessage = '',
}) => {
  const [inputValue, setInputValue] = useState('');

  const handleAddItem = () => {
    if (!inputValue.trim()) return;
    if (items.length >= maxItems) return;
    
    const newItems = [...items, inputValue.trim()];
    onChange(newItems);
    setInputValue('');
  };

  const handleRemoveItem = (index) => {
    const newItems = items.filter((_, i) => i !== index);
    onChange(newItems);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddItem();
    }
  };

  const canAddMore = items.length < maxItems;
  const meetsMinimum = items.length >= minItems;

  return (
    <div style={{ marginBottom: EMZI_SPACING.md }}>
      <label style={{
        display: 'block',
        fontSize: '14px',
        fontWeight: '500',
        marginBottom: EMZI_SPACING.xs,
        color: EMZI_COLORS.textPrimary,
      }}>
        {label}
        {required && <span style={{ color: EMZI_COLORS.error, marginLeft: '4px' }}>*</span>}
        <span style={{ 
          fontSize: '12px', 
          fontWeight: '400',
          color: EMZI_COLORS.textSecondary,
          marginLeft: EMZI_SPACING.xs 
        }}>
          ({items.length}/{maxItems} items{minItems > 0 && `, min: ${minItems}`})
        </span>
      </label>

      {helper && (
        <div style={{
          fontSize: '12px',
          color: EMZI_COLORS.textSecondary,
          marginBottom: EMZI_SPACING.xs,
          padding: EMZI_SPACING.xs,
          background: EMZI_COLORS.surface,
          borderRadius: EMZI_RADIUS.sm,
          borderLeft: `3px solid ${EMZI_COLORS.primary}`,
        }}>
          💡 {helper}
        </div>
      )}

      {/* Input field */}
      <div style={{ display: 'flex', gap: EMZI_SPACING.xs, marginBottom: EMZI_SPACING.xs }}>
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={placeholder}
          disabled={!canAddMore}
          style={{
            flex: 1,
            padding: EMZI_SPACING.sm,
            border: `1px solid ${hasError ? EMZI_COLORS.error : EMZI_COLORS.border}`,
            borderRadius: EMZI_RADIUS.md,
            fontSize: '14px',
            outline: 'none',
            background: canAddMore ? EMZI_COLORS.background : EMZI_COLORS.surface,
          }}
        />
        <button
          type="button"
          onClick={handleAddItem}
          disabled={!inputValue.trim() || !canAddMore}
          style={{
            padding: `${EMZI_SPACING.sm} ${EMZI_SPACING.md}`,
            background: canAddMore && inputValue.trim() ? EMZI_COLORS.primary : EMZI_COLORS.surface,
            color: canAddMore && inputValue.trim() ? '#FFFFFF' : EMZI_COLORS.textMuted,
            border: 'none',
            borderRadius: EMZI_RADIUS.md,
            fontSize: '14px',
            fontWeight: '500',
            cursor: canAddMore && inputValue.trim() ? 'pointer' : 'not-allowed',
          }}
        >
          Add
        </button>
      </div>

      {/* Items list */}
      {items.length > 0 && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: EMZI_SPACING.xs,
          maxHeight: '200px',
          overflowY: 'auto',
        }}>
          {items.map((item, index) => (
            <div
              key={`item-${index}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: EMZI_SPACING.sm,
                background: EMZI_COLORS.surface,
                borderRadius: EMZI_RADIUS.sm,
                fontSize: '14px',
              }}
            >
              <span style={{ flex: 1, color: EMZI_COLORS.textPrimary }}>{item}</span>
              <button
                type="button"
                onClick={() => handleRemoveItem(index)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: EMZI_COLORS.error,
                  cursor: 'pointer',
                  fontSize: '16px',
                  padding: '4px',
                }}
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Validation messages */}
      {hasError && errorMessage && (
        <div style={{
          marginTop: EMZI_SPACING.xs,
          fontSize: '12px',
          color: EMZI_COLORS.error,
        }}>
          {errorMessage}
        </div>
      )}
      
      {!meetsMinimum && !hasError && minItems > 0 && (
        <div style={{
          marginTop: EMZI_SPACING.xs,
          fontSize: '12px',
          color: EMZI_COLORS.warning,
        }}>
          ⚠️ Please add at least {minItems - items.length} more item(s)
        </div>
      )}
    </div>
  );
};

export default StructuredListInput;
